import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";

const cinzel = "font-['Cinzel',serif]";
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/**
 * Drag / touch / keyboard before-after slider.
 * - Pointer events + pointer capture (mouse, touch and pen with one code path)
 * - touch-action: pan-y keeps vertical page scrolling working on mobile
 * - The handle is a focusable role="slider" (←/→ = 5%, Shift = 15%, Home/End)
 */
export default function CompareSlider({ before, after, altBefore, altAfter, className = "" }) {
  const [pos, setPos] = useState(50);
  const boxRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromX = (clientX) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    setPos(clamp(((clientX - rect.left) / rect.width) * 100, 0, 100));
  };

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) updateFromX(e.clientX);
  };
  const endDrag = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onKeyDown = (e) => {
    const step = e.shiftKey ? 15 : 5;
    if (e.key === "ArrowLeft") { e.preventDefault(); setPos((p) => clamp(p - step, 0, 100)); }
    else if (e.key === "ArrowRight") { e.preventDefault(); setPos((p) => clamp(p + step, 0, 100)); }
    else if (e.key === "Home") { e.preventDefault(); setPos(0); }
    else if (e.key === "End") { e.preventDefault(); setPos(100); }
  };

  return (
    <div
      ref={boxRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className={`relative w-full select-none overflow-hidden rounded-xl bg-[#0e0905] ring-1 ring-[#c9a84c]/30 ${className}`}
      style={{ touchAction: "pan-y", cursor: "col-resize" }}
    >
      {/* AFTER underneath, BEFORE clipped on top */}
      <img src={after} alt={altAfter} draggable={false} className="absolute inset-0 h-full w-full object-contain object-top" />
      <img
        src={before}
        alt={altBefore}
        draggable={false}
        className="absolute inset-0 h-full w-full object-contain object-top"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      {/* labels */}
      <span
        className={`${cinzel} pointer-events-none absolute left-3 top-3 rounded-full border border-white/15 bg-black/70 px-3 py-1 text-[9px] tracking-[0.18em] text-white/85 backdrop-blur-sm transition-opacity duration-200 motion-reduce:transition-none`}
        style={{ opacity: pos > 12 ? 1 : 0 }}
      >
        BEFORE
      </span>
      <span
        className={`${cinzel} pointer-events-none absolute right-3 top-3 rounded-full bg-[#c9a84c]/90 px-3 py-1 text-[9px] font-bold tracking-[0.18em] text-[#1a0f02] transition-opacity duration-200 motion-reduce:transition-none`}
        style={{ opacity: pos < 88 ? 1 : 0 }}
      >
        AFTER
      </span>

      {/* divider line */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 top-0 w-0.5 bg-[linear-gradient(to_bottom,rgba(201,168,76,0.1),#c9a84c_30%,#c9a84c_70%,rgba(201,168,76,0.1))]"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      />

      {/* handle */}
      <div
        role="slider"
        tabIndex={0}
        aria-label="Reveal before and after"
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-valuetext={`${Math.round(pos)}% before visible`}
        onKeyDown={onKeyDown}
        className="absolute top-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#c9a84c]/60 bg-white text-[#1a0f02] shadow-[0_4px_20px_rgba(0,0,0,0.4)] outline-none focus-visible:ring-2 focus-visible:ring-[#ffe9a0] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0e0905]"
        style={{ left: `${pos}%`, transform: "translate(-50%, -50%)", cursor: "ew-resize" }}
      >
        <ChevronsLeftRight size={18} strokeWidth={2.2} aria-hidden="true" />
      </div>

      <span
        className={`pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/60 px-4 py-1.5 text-[9px] tracking-[0.1em] text-white/70 backdrop-blur-sm`}
      >
        DRAG TO REVEAL
      </span>
    </div>
  );
}